<article class="brandbot-card no-hover flex flex-col h-full rounded-2xl overflow-hidden">
  <div class="relative z-20 px-5 pt-5 pb-3 flex items-start justify-between gap-3">
    <div class="flex flex-col min-w-0 gap-1.5">
      <div class="flex items-baseline gap-2 min-w-0">
        <h3
          class="text-pc-eyebrow font-bold uppercase tracking-widest leading-tight text-slate-500 dark:text-neutral-500 shrink-0"
        >
          <!-- Not CLOSED_PER_MONTH any more: the headline beside it is the
               whole window's total, so a title reading "per month" left the
               reader asking whether 208.168 was the year or one month. The
               chart below is still monthly and its axis says so. -->
          {{ 'CLOSED_BUSINESS' | translate }}
        </h3>
        <!-- The one place the year is written. See `windowLabel`. -->
        @if (windowLabel(); as window) {
          <span
            class="text-pc-label font-medium capitalize tabular-nums text-[color:var(--text-subtitle)] truncate"
          >
            {{ window }}
          </span>
        }
      </div>
      @if (hasData()) {
        <div class="flex flex-wrap items-baseline gap-x-2">
          <span
            class="text-[22px] 2xl:text-[27px] font-semibold tracking-tight tabular-nums leading-none text-[color:var(--text-title)]"
          >
            {{ heroLabel() }}
          </span>
          @if (delta(); as d) {
            <span
              class="inline-flex items-center gap-0.5 text-pc-label font-semibold tabular-nums {{
                d.classes
              }}"
              [title]="'PERIOD_DELTA_TOOLTIP' | translate: d.tooltipParams"
            >
              <primecontrol-svg-icon
                class="icon-size-3.5"
                [svgIcon]="d.icon"
              ></primecontrol-svg-icon>
              {{ d.label }}
            </span>
          }
        </div>
      }
    </div>

    <!-- Always rendered, unlike the dropdown it replaces: an instance with no
         closed business yet still needs a way OUT of the empty window it landed
         on. Hiding the control behind `hasData` made "no hay datos" look like a
         verdict on the whole history. -->
    <primecontrol-select
      class="w-[11rem] shrink-0"
      size="small"
      [control]="periodControl"
      [options]="periodOptions"
      (change)="onPeriodChange()"
    ></primecontrol-select>
  </div>

  @if (hasData()) {
    <!-- Readout: the hovered month, or the best one at rest. A row of its own
         above the plot so it never covers the bars — least of all the bar being
         read. See `readout`.
         A hairline and not a boxed strip: boxed, it stacked under the header as
         a second header, and the card read as two title blocks before any chart.
         The rule ties it downward, to the chart it describes. -->
    @if (readout(); as line) {
      <div
        class="mx-5 mt-1 mb-4 pt-3 border-t border-neutral-200/60 dark:border-white/10 flex flex-wrap items-center gap-x-5 gap-y-1 text-pc-label text-[color:var(--text-subtitle)]"
      >
        <span class="inline-flex items-baseline gap-1.5 whitespace-nowrap">
          @if (line.caption; as caption) {
            <span class="opacity-70">{{ caption | translate }}</span>
          }
          <span class="font-semibold capitalize text-[color:var(--text-title)]">
            {{ line.month }}
          </span>
        </span>
        <span class="inline-flex items-center gap-1.5 tabular-nums whitespace-nowrap">
          <span
            class="inline-block w-2 h-2 rounded-sm"
            style="background: var(--pc-accent-base)"
          ></span>
          {{ 'LEGEND_ORDERS' | translate }}
          <span class="font-semibold text-[color:var(--text-title)]">{{ line.orderLabel }}</span>
        </span>
        <span class="inline-flex items-center gap-1.5 tabular-nums whitespace-nowrap">
          <span class="legend-point inline-block w-1.5 h-1.5 rounded-full"></span>
          {{ 'LEGEND_APPROVED_QUOTES' | translate }}
          <span class="font-semibold text-[color:var(--text-title)]">{{ line.approvedLabel }}</span>
        </span>
        <!-- The comparison only earns a slot once there is something to compare. -->
        @if (line.previousLabel; as before) {
          <span class="inline-flex items-center gap-1.5 tabular-nums whitespace-nowrap">
            <span class="closed-ghost-swatch inline-block w-2 h-2 rounded-sm"></span>
            {{ comparisonLabel() }}
            <span class="font-semibold text-[color:var(--text-title)]">{{ before }}</span>
          </span>
        }
      </div>
    }

    <div class="px-5 flex-1 min-h-0 flex flex-col justify-end">
      <!-- The plot's height, and the card's: everything inside is positioned in percentages,
           so this one measurement is the whole geometry and the card is the sum of its parts.
           In the SCSS and no longer as `h-44 2xl:h-56` — the taller step needed a `2xl:`
           variant Tailwind does not emit here (`2xl:h-72` resolves to 0px), and a height
           this card's proportions depend on should not be silently droppable. -->
      <div class="closed-plot relative flex" (mouseleave)="onPlotLeave()">
        <!-- Y axis. The ticks and the gridlines share one list of percentages,
             so a figure always names the line it sits on — the old decorative
             gradient drew four lines that stood for nothing. -->
        <div class="relative w-12 shrink-0" aria-hidden="true">
          @for (tick of axisTicks(); track tick.pct) {
            <span
              class="absolute right-2 -translate-y-1/2 text-pc-eyebrow tabular-nums text-[color:var(--text-subtitle)]"
              [style.top.%]="tick.pct"
            >
              {{ tick.label }}
            </span>
          }
        </div>

        <div class="relative flex-1 min-w-0">
          @for (tick of axisTicks(); track tick.pct) {
            <span class="closed-gridline absolute inset-x-0" [style.top.%]="tick.pct"></span>
          }
          <div class="relative flex items-end justify-between gap-1 sm:gap-2 h-full">
            @for (bar of bars(); track bar.fullLabel; let i = $index) {
              <!-- The whole column is the target, not the bar: an empty month is
                 zero pixels tall, and a month worth reading about is exactly the
                 one you cannot point at. `closed-col` paints the hover band.
                 A month with orders links into the listing filtered to it — the
                 question that follows "¿qué pasó en febrero?". A month with none
                 stays inert: it would land on an empty table. -->
              <a
                [routerLink]="barLink(bar, i)"
                [queryParams]="bar.orderAmount > 0 ? bar.linkParams : null"
                class="closed-col relative flex-1 h-full flex items-end"
                [class.cursor-pointer]="bar.orderAmount > 0"
                [class.cursor-default]="bar.orderAmount === 0"
                [class.closed-col--active]="hovered() === i"
                [class.closed-col--dimmed]="hovered() !== null && hovered() !== i"
                (mouseenter)="hovered.set(i)"
                (click)="onBarClick($event, i)"
                (focus)="hovered.set(i)"
                (blur)="hovered.set(null)"
                [attr.aria-label]="
                  bar.fullLabel +
                  ': ' +
                  ('LEGEND_ORDERS' | translate) +
                  ' ' +
                  bar.orderLabel +
                  ', ' +
                  ('LEGEND_APPROVED_QUOTES' | translate) +
                  ' ' +
                  bar.approvedLabel
                "
              >
                <!-- The accent belongs to the months that closed business. The
                   current one is drawn softer because it is still running, not
                   greyed out — that rule ran the other way round, so a year
                   whose only sale was in February showed February in dead grey
                   and gave the colour to an empty August. -->
                <!-- Last year, behind and hollow. Absolute so it shares the
                   column's baseline with the solid bar instead of pushing it
                   sideways; the solid one keeps its place in the flow. -->
                @if (bar.previousHeight > 0) {
                  <span
                    class="closed-ghost absolute inset-x-0 bottom-0 rounded-t-md"
                    [style.height.%]="bar.previousHeight"
                    aria-hidden="true"
                  ></span>
                }
                <div
                  class="closed-bar relative w-full rounded-t-md"
                  [class.closed-bar--current]="bar.current && bar.height > 0"
                  [style.height.%]="bar.height"
                ></div>
                @if (bar.hasLine) {
                  <span class="closed-point" [style.bottom.%]="bar.lineHeight"></span>
                }
              </a>
            }
          </div>
        </div>
      </div>
      <div class="closed-baseline mt-1.5 ml-12"></div>
    </div>

    <!-- Every month gets its tick, on the same flex split as the columns above
         so each label sits under its own bar. The two-end form said "sept 25 →
         ago 26" and left the middle unreadable. -->
    <!-- pl matches px-5 (1.25rem) plus the axis gutter (w-12 = 3rem), so each
         month tick lands under its own column. -->
    <div class="px-5 pl-[4.25rem] pt-2 flex items-end justify-between gap-1 sm:gap-2">
      @for (bar of bars(); track bar.fullLabel; let i = $index) {
        <span
          class="flex-1 min-w-0 text-center text-pc-eyebrow capitalize truncate transition-colors"
          [ngClass]="
            hovered() === i
              ? 'text-[color:var(--text-title)] font-semibold'
              : 'text-[color:var(--text-subtitle)]'
          "
        >
          {{ bar.label }}
        </span>
      }
    </div>

    <!-- The legend is gone: the readout strip above the chart already names each
         series next to its swatch AND gives it a figure, so this row repeated
         half of it and was the third place on one card printing the same words.
         What only lived here was the note about when the approved series starts,
         which is a caveat about the data, not a key to the colours. -->
    @if (lineStartLabel(); as since) {
      <p class="px-5 pb-5 pt-3 text-pc-label text-[color:var(--text-subtitle)]">
        {{ 'LEGEND_APPROVED_QUOTES' | translate }}:
        {{ 'LEGEND_SINCE' | translate: { month: since } }}
      </p>
    } @else {
      <div class="pb-5"></div>
    }
  } @else if (loading()) {
    <div
      class="pc-skeleton-card relative px-5 pb-5 flex-1 min-h-0 flex items-center justify-center"
      aria-hidden="true"
    >
      <div class="pc-skeleton-aurora"></div>
      <div class="pc-skeleton-ring"></div>
      <div class="relative z-10 w-full h-full flex items-center justify-center">
        <div class="pc-skeleton-tile">
          <div class="pc-skeleton-tile-core"></div>
        </div>
      </div>
    </div>
  } @else if (error()) {
    <div class="px-5 pb-5 flex-1 min-h-0 flex items-center justify-center">
      <primecontrol-empty-data-particules
        [message]="'DASHBOARD_LOAD_ERROR'"
        [icon]="'heroicons_outline:exclamation-circle'"
      ></primecontrol-empty-data-particules>
    </div>
  } @else {
    <!-- Not the `compact` variant embedded surfaces usually take: this card
         keeps the chart's full height even with nothing to plot, so the compact
         drawing left a 48px disc floating in ~300px of empty card and read as a
         rendering glitch rather than a state. The page-sized state fills the
         room the card already reserved. -->
    <div class="px-5 pb-5 flex-1 min-h-0 flex items-center justify-center">
      <primecontrol-empty-data-particules
        [message]="'NO_CLOSED_DATA'"
        [icon]="'heroicons_outline:chart-bar'"
      ></primecontrol-empty-data-particules>
    </div>
  }
</article>
